import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, Award, BookOpen, CheckCircle, Clock, ChevronRight, 
  Shield, AlertTriangle, Play, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const TrainingCategory = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchCategory();
  }, [categorySlug]);

  const fetchCategory = async () => {
    try {
      const res = await api.get(`/training/categories/${categorySlug}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load category');
      navigate('/talent/training');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const { category, modules } = data || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-category-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/talent/training')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Training Hub
            </Button>
          </div>
          <h1 className="text-2xl font-bold mt-4">{category?.name}</h1>
          <p className="text-white/80 mt-1">{category?.description}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">
            {modules?.length || 0} Training Module{modules?.length !== 1 ? 's' : ''}
          </h2>
        </div>

        <div className="space-y-4">
          {modules?.map((module, index) => {
            const isCertified = module.certification?.earned;
            const isInProgress = module.progress.status === 'in_progress';
            const isSafetyCritical = module.is_safety_critical;
            
            return (
              <Card 
                key={module.id}
                className={`cursor-pointer hover:shadow-md transition-all ${
                  isCertified ? 'border-green-300 bg-green-50/50' : ''
                }`}
                onClick={() => navigate(`/talent/training/module/${module.id}`)}
                data-testid={`module-${module.slug}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Number Badge */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      isCertified 
                        ? 'bg-green-500 text-white' 
                        : isInProgress 
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isCertified ? <CheckCircle className="w-5 h-5" /> : index + 1}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            {module.name}
                            {isSafetyCritical && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Safety Critical
                              </Badge>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                      
                      {/* Module Stats */}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {module.lesson_count} lessons
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {module.estimated_minutes} min
                        </span>
                        <span>
                          Pass: {module.pass_score}%{isSafetyCritical && ' (100% for safety)'}
                        </span>
                      </div>
                      
                      {/* Progress or Certification */}
                      <div className="mt-4">
                        {isCertified ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-700 border-green-300">
                              <Award className="w-4 h-4 mr-1" />
                              {module.badge_name}
                            </Badge>
                            <span className="text-sm text-green-600">
                              Score: {module.certification.score}%
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDateMonthDay(module.certification.passed_at)}
                            </span>
                          </div>
                        ) : isInProgress ? (
                          <div className="flex items-center gap-3">
                            <Progress value={module.progress.progress_percent} className="flex-1 h-2" />
                            <span className="text-sm text-purple-600 font-medium">
                              {module.progress.progress_percent}%
                            </span>
                            <Button size="sm" variant="outline" className="text-purple-600 border-purple-300">
                              <Play className="w-4 h-4 mr-1" />
                              Continue
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                            <Play className="w-4 h-4 mr-1" />
                            Start Training
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {modules?.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No training modules available in this category yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrainingCategory;
